import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Address, ChainId } from '@31third/common';
import { ChainRepository, SignerService } from 'common';
import {
  EnzymeRepository,
  RebalancingRepository,
  TransactionExecutionRepository,
} from '../repository';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  constructor(
    private readonly chainRepository: ChainRepository,
    private readonly signerService: SignerService,
    private readonly enzymeRepository: EnzymeRepository,
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly transactionExecutionRepository: TransactionExecutionRepository,
  ) {}

  public captureError(err: Error): void {
    Sentry.captureException(err, {
      tags: this.prepareTags(),
    });
  }

  public captureMsg(msg: string): void {
    Sentry.captureMessage(msg, {
      tags: this.prepareTags(),
    });
  }

  private prepareTags(): {
    chainId?: ChainId;
    signer?: Address;
    enzymeVault?: Address;
    rebalancingType?: 'WALLET' | 'ENZYME';
    rebalancingId?: string;
    tenderlyLink?: string;
  } {
    const tags: SentryTags = {};

    const chain = this.chainRepository.store.getValue().chain;
    if (chain) {
      tags.chainId = chain.identifier;
    }

    const signer = this.signerService.getSigner();
    if (signer) {
      tags.signer = signer.address;
    }

    const vault = this.enzymeRepository.store.getValue().selectedVault;
    if (vault) {
      tags.enzymeVault = vault.address;
      tags.rebalancingType = 'ENZYME';
    } else {
      tags.rebalancingType = 'WALLET';
    }

    const rebalancing = this.rebalancingRepository.store.getValue().rebalancing;
    if (rebalancing) {
      tags.rebalancingId = rebalancing.id;
    }

    const tenderlySimulation =
      this.transactionExecutionRepository.store.getValue().tenderlySimulation;
    if (tenderlySimulation) {
      tags.tenderlyLink = tenderlySimulation.getPublicUrl();
    }

    return tags;
  }
}

interface SentryTags {
  chainId?: ChainId;
  signer?: Address;
  enzymeVault?: Address;
  rebalancingType?: 'WALLET' | 'ENZYME';
  rebalancingId?: string;
  tenderlyLink?: string;
}
